import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserById } from 'services/userService';
import {
  getTarefasWithUsersAndProjetoByUser,
  updateTarefaAPI,
} from 'api/requestsApi';
import {
  arquivarTarefa,
  deleteTarefa,
  updateTarefaStatus,
} from 'services/tarefaService';
import { confirmTarefaDelete } from 'components/Tarefa/confirmTarefaDelete';
import { User } from 'types/user';
import {
  TarefaWithUserAndProjetoDTO,
  TarefaUpdateFormData,
  TarefaInsertFormData,
  TarefaStatus,
} from 'types/tarefa';
import UserTarefaTable from 'components/User/UserTarefaTable';
import UserAvatar from 'components/User/UserAvatar';
import TarefaModal from 'components/Tarefa/TarefaModal';
import TarefaDetailsCard from 'components/Tarefa/TarefaDetailsCard';
import NotificationBadge from 'components/NotificationBox/NotificationBadge';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faClock } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext'; // Add this import
import { useNotification } from 'NotificationContext';
import { getUnreadCountsAPI } from 'api/notificationsApi';
import { useUserTarefaFilters } from 'hooks/useFilterState';
import { toast } from 'react-toastify';
import './userStyles.scss';

const PAGE_SIZE = 20;

const UsersTarefasPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth(); // Get current user from context
  const { loadStoredNotifications } = useNotification();
  const [user, setUser] = useState<User | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [tarefas, setTarefas] = useState<TarefaWithUserAndProjetoDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tasksLoading, setTasksLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showTarefaModal, setShowTarefaModal] = useState(false);
  const [selectedTarefa, setSelectedTarefa] =
    useState<TarefaWithUserAndProjetoDTO | null>(null);
  const [showDetailsCard, setShowDetailsCard] = useState(false);
  const [tarefaForDetails, setTarefaForDetails] =
    useState<TarefaWithUserAndProjetoDTO | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState<string>('id');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  const {
    filters,
    appliedFilters,
    isFiltered,
    updateFilter,
    applyFilters,
    clearFilters,
  } = useUserTarefaFilters();

  const fetchTarefas = useCallback(
    async (parsedUserId: number) => {
      setTasksLoading(true);
      try {
        const userTarefas = await getTarefasWithUsersAndProjetoByUser(
          parsedUserId,
          page,
          PAGE_SIZE,
          isFiltered ? appliedFilters : undefined,
          sortField,
          sortDirection
        );
        setTarefas(userTarefas.content);
        setTotalPages(userTarefas.totalPages);
      } catch (err) {
        console.error('Error fetching tarefas:', err);
        toast.error('Erro ao carregar tarefas');
      } finally {
        setTasksLoading(false);
      }
    },
    [page, isFiltered, appliedFilters, sortField, sortDirection]
  );

  // Fetch user profile once per userId
  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (!userId || !currentUser) return;

        // If Employee and viewing own page, use /users/me
        const isEmployee =
          currentUser.roles &&
          Array.isArray(currentUser.roles) &&
          currentUser.roles.some(
            (role: any) =>
              role.authority === 'ROLE_EMPLOYEE' || role === 'ROLE_EMPLOYEE'
          );
        const isOwnPage = currentUser.id?.toString() === userId;

        let userData;
        if (isEmployee && isOwnPage) {
          // Fetch from /users/me
          const { getCurrentUserWithRoles } = await import(
            'services/userService'
          );
          userData = await getCurrentUserWithRoles();
        } else {
          // Fetch from /users/{id}
          const parsedUserId = parseInt(userId, 10);
          userData = await getUserById(parsedUserId);
        }

        setUser(userData);
      } catch (err) {
        setError('Failed to fetch user data or tarefas');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId, currentUser]);

  // Fetch unread notification count for this user
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!userId) return;
      try {
        const counts = await getUnreadCountsAPI([parseInt(userId, 10)]);
        setUnreadCount(counts[parseInt(userId, 10)] ?? 0);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };
    fetchUnreadCount();
  }, [userId]);

  // Fetch tarefas whenever the user, page or applied filters change
  useEffect(() => {
    if (!userId) return;
    fetchTarefas(parseInt(userId, 10));
  }, [userId, fetchTarefas]);

  const handleApplyFilters = useCallback(() => {
    const success = applyFilters();
    if (!success) {
      toast.warning('Por favor, preencha pelo menos um filtro para aplicar');
      return;
    }
    setPage(0);
  }, [applyFilters]);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    setPage(0);
  }, [clearFilters]);

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setSortField(field);
        setSortDirection('ASC');
      }
      setPage(0);
    },
    [sortField]
  );

  const handleNavigateToCalendar = () => {
    navigate(`/user-calendar/${userId}`);
  };

  const handleNavigateToProjetoHistory = () => {
    navigate(`/users/${userId}/projeto-history`);
  };

  const handleNavigateToNotifications = async () => {
    if (!userId) return;
    const parsedUserId = parseInt(userId, 10);
    await loadStoredNotifications(parsedUserId);
    navigate(`/notifications/${parsedUserId}`);
  };

  const handleNavigateToBancoHoras = () => {
    navigate(`/users/${userId}/banco-horas`);
  };

  const handleEditTarefa = (tarefaId: number) => {
    const tarefa = tarefas.find((t) => t.id === tarefaId);
    if (tarefa) {
      setSelectedTarefa(tarefa);
      setShowTarefaModal(true);
    }
  };

  const handleDeleteTarefa = async (tarefaId: number) => {
    if (!confirmTarefaDelete()) return;
    try {
      await deleteTarefa(tarefaId);
      toast.success('Tarefa apagada com sucesso!');
      if (userId) {
        await fetchTarefas(parseInt(userId, 10));
      }
    } catch (error) {
      console.error('Erro ao apagar tarefa:', error);
      toast.error('Erro ao apagar tarefa');
    }
  };

  const handleViewDetails = (tarefaId: number) => {
    const tarefa = tarefas.find((t) => t.id === tarefaId);
    if (tarefa) {
      setTarefaForDetails(tarefa);
      setShowDetailsCard(true);
    }
  };

  const handleCloseTarefaModal = () => {
    setShowTarefaModal(false);
    setSelectedTarefa(null);
  };

  const handleArchiveTarefa = async (tarefaId: number) => {
    try {
      await arquivarTarefa(tarefaId);
      handleCloseTarefaModal();
      toast.success('Tarefa arquivada com sucesso!');
      if (userId) {
        await fetchTarefas(parseInt(userId, 10));
      }
    } catch (error) {
      console.error('Erro ao arquivar tarefa:', error);
      toast.error(
        error instanceof Error ? error.message : 'Erro ao arquivar tarefa'
      );
    }
  };

  const handleStatusChange = async (
    tarefaId: number,
    newStatus: TarefaStatus
  ) => {
    try {
      await updateTarefaStatus(tarefaId, newStatus);
      if (userId) {
        await fetchTarefas(parseInt(userId, 10));
      }
      toast.success('Status da tarefa atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar status da tarefa:', error);
      toast.error('Erro ao atualizar status da tarefa');
    }
  };

  const handleSaveTarefa = async (
    formData: TarefaInsertFormData | TarefaUpdateFormData
  ) => {
    try {
      if ('id' in formData) {
        await updateTarefaAPI(formData.id, formData);
        if (userId) {
          await fetchTarefas(parseInt(userId, 10));
        }
      } else {
        // Handle insert case if needed
        console.log('Insert case not handled in this context');
      }
      handleCloseTarefaModal();
    } catch (error) {
      console.error('Error updating tarefa:', error);
      // Handle error (e.g., show error message to user)
    }
  };

  if (loading) {
    return <div className="loading-container">A carregar...</div>;
  }

  if (error) {
    return <div className="error-container">Erro: {error}</div>;
  }

  if (!user) {
    return (
      <div className="not-found-container">Colaborador não encontrado</div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-shell">
        <div className="page-title-container page-title-container--scroll-table">
          <div
            className="user-info-container"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <div
              className="user-image-container"
              style={{ marginRight: '1.5rem' }}
            >
              <UserAvatar userId={user.id} name={user.name} size={100} rounded />
            </div>
            <div className="user-details">
              <h2
                className="page-title page-title--user-header"
                style={{ margin: 0 }}
              >
                {user.name}
              </h2>
              <p className="user-email" style={{ margin: 0 }}>
                {user.email}
              </p>
            </div>
          </div>
          <div
            className="page-actions d-flex gap-2"
            style={{ alignSelf: 'flex-start' }}
          >
            <Button variant="primary" onClick={handleNavigateToCalendar}>
              Ver Calendário
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleNavigateToProjetoHistory}
            >
              Ver Histórico de Desempenho
            </Button>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button
                variant="outline-primary"
                onClick={handleNavigateToNotifications}
              >
                <FontAwesomeIcon icon={faBell} className="me-2" />
                Ver Notificações
              </Button>
              <NotificationBadge unreadCount={unreadCount} />
            </div>
            <Button
              variant="outline-primary"
              onClick={handleNavigateToBancoHoras}
            >
              <FontAwesomeIcon icon={faClock} className="me-2" />
              Banco de Horas
            </Button>
          </div>
        </div>

        {/* Content wrapped in a div with the same width */}
        <div style={{ width: '100%', marginTop: '3rem' }}>
          <div className="table-responsive">
            <h3
              className="section-title user-tasks-section-title"
              style={{
                borderBottom: 'none',
                paddingBottom: 0,
                position: 'relative',
                background: 'transparent',
                marginLeft: 0,
                paddingLeft: 0,
                textAlign: 'left',
              }}
            >
              Tarefas atribuídas
            </h3>
            <UserTarefaTable
              tarefas={tarefas}
              onEditTarefa={handleEditTarefa}
              onDeleteTarefa={handleDeleteTarefa}
              onViewDetails={handleViewDetails}
              isLoading={tasksLoading}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              filters={filters}
              updateFilter={updateFilter}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
              isFiltered={isFiltered}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>
        </div>
      </div>

      {showTarefaModal && selectedTarefa && (
        <TarefaModal
          show={showTarefaModal}
          onHide={handleCloseTarefaModal}
          tarefa={selectedTarefa}
          onSave={handleSaveTarefa}
          onStatusChange={handleStatusChange}
          onArchive={handleArchiveTarefa}
          isEditing={true}
        />
      )}
      {showDetailsCard && tarefaForDetails && (
        <TarefaDetailsCard
          tarefa={tarefaForDetails}
          onClose={() => setShowDetailsCard(false)}
        />
      )}
    </div>
  );
};

export default UsersTarefasPage;
